import { ORPCError } from "@orpc/client";
import type {
  ModerationCategory,
  ModerationInputKind,
  ModerationRefusal,
  ModerationSurface,
  ModerationWarning,
  SurfaceBan,
} from "@pax/api-contract/moderation/v1alpha1";
import {
  APPEAL_COPY,
  automodUnavailable,
  BAN_COPY,
  CRISIS_RESOURCES_URL,
  INPUT_WORDS,
  type InputWords,
  REFUSAL_WARNINGS,
} from "@pax/moderation-copy";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@pax/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, orpc, reachable, requestErrorMessage } from "@/lib/api";
import { BanAppeal } from "./BanAppeal";
import { moderationRefusal, smallWarning, surfaceBan } from "./refusals";

const IMAGE_INPUTS: readonly ModerationInputKind[] = [
  "profile_avatar",
  "profile_banner",
  "asset_media",
  "preset_image",
];

const ALERT = "rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive";

/**
 * Why a save failed, for a form that saves through automod: a refusal shows its warnings and, when
 * it can be appealed, a way to appeal it; a ban shows the ban copy. `inputs` names what the form
 * saves, for when automod is down, and `words` names what was refused when its input kind alone
 * doesn't, as for a preset judged whole.
 */
export function SubmitError({
  error,
  inputs,
  words,
  canAppeal = true,
}: {
  error: unknown;
  inputs: string;
  words?: InputWords;
  /** Whether you may appeal what's refused, where not everyone may, as on a preset's team. */
  canAppeal?: boolean;
}) {
  if (!error) return null;
  const refusal = moderationRefusal(error);
  if (refusal) {
    return (
      <RefusalNotice
        key={refusal.classificationId}
        refusal={refusal}
        words={words}
        canAppeal={canAppeal}
      />
    );
  }
  const ban = surfaceBan(error);
  if (ban) {
    return (
      <div role="alert" className={ALERT}>
        <BanLine ban={ban} />
      </div>
    );
  }
  return (
    <p role="alert" className={ALERT}>
      {error instanceof ORPCError && error.code === "AUTOMOD_UNAVAILABLE"
        ? automodUnavailable(inputs)
        : requestErrorMessage(error)}
    </p>
  );
}

function RefusalNotice({
  refusal,
  words = INPUT_WORDS[refusal.inputKind],
  canAppeal,
}: {
  refusal: ModerationRefusal;
  words?: InputWords;
  canAppeal: boolean;
}) {
  const [warningOpen, setWarningOpen] = useState(
    refusal.warning === "big" || refusal.warning === "crisis_resources",
  );
  return (
    <div role="alert" className={ALERT}>
      <p>{smallWarning(refusal)}</p>
      <AppealLine refusal={refusal} words={words} allowed={canAppeal} />
      <StandingLine surface={words.surface} />
      <RefusalWarning
        warning={refusal.warning}
        categories={refusal.categories.map(({ category }) => category)}
        words={words}
        open={warningOpen}
        onOpenChange={setWarningOpen}
      />
    </div>
  );
}

/** The way to appeal a refusal, or why it can't be. `onAppealed` runs once the appeal is filed. */
export function AppealLine({
  refusal,
  words,
  onAppealed,
  allowed = true,
}: {
  refusal: ModerationRefusal;
  words: InputWords;
  onAppealed?: () => unknown;
  /** Whether you may appeal it, where not everyone may, as on a preset's team. */
  allowed?: boolean;
}) {
  const appeal = useMutation({
    mutationFn: () =>
      reachable(
        api.moderation.appeals.create({ body: { classificationId: refusal.classificationId } }),
      ),
    onSuccess: () => onAppealed?.(),
  });
  if (!refusal.appealable) {
    const sexualFile =
      IMAGE_INPUTS.includes(refusal.inputKind) &&
      refusal.categories.some(({ category }) => category === "sexual_content");
    return (
      <p className="mt-1 text-destructive/80">
        {sexualFile ? APPEAL_COPY.unavailableSexualContent : APPEAL_COPY.unavailable}
      </p>
    );
  }
  if (!allowed) return <p className="mt-1 text-destructive/80">{APPEAL_COPY.notYourRoles}</p>;
  if (appeal.isSuccess) return <p className="mt-1 text-foreground">{APPEAL_COPY.submitted}</p>;
  return (
    <div className="mt-1 flex flex-wrap items-center gap-x-3">
      <Button
        type="button"
        variant="link"
        size="sm"
        className="h-auto px-0 text-destructive underline"
        disabled={appeal.isPending}
        onClick={() => appeal.mutate()}
      >
        {APPEAL_COPY.cta}
      </Button>
      {appeal.error ? (
        <span className="text-destructive/80">
          {appeal.error instanceof ORPCError && appeal.error.code === "CONFLICT"
            ? APPEAL_COPY.oneAtATime(words)
            : requestErrorMessage(appeal.error)}
        </span>
      ) : null}
    </div>
  );
}

function BanLine({ ban }: { ban: SurfaceBan }) {
  return (
    <>
      <p>
        <strong className="font-medium">{BAN_COPY.bannedTitle}</strong> {BAN_COPY.banned(ban)}
      </p>
      <BanAppeal ban={ban} />
    </>
  );
}

/**
 * Where the account stands on the refused input's surface once the refusal has counted: banned, or
 * close to a ban. Read fresh, since the refusal may have just changed it.
 */
function StandingLine({ surface }: { surface: ModerationSurface }) {
  const standing = useQuery({ ...orpc.moderation.standing.get.queryOptions(), staleTime: 0 });
  const entry = standing.data?.surfaces.find(candidate => candidate.surface === surface);
  if (!entry) return null;
  if (entry.ban) {
    return (
      <div className="mt-1">
        <BanLine ban={{ surface, ...entry.ban, categories: entry.categories }} />
      </div>
    );
  }
  if (!entry.nearBan) return null;
  return (
    <p className="mt-1">
      <strong className="font-medium">{BAN_COPY.approachingTitle}</strong>{" "}
      {BAN_COPY.approaching(entry.categories)}
    </p>
  );
}

/** The pop-up a big refusal interrupts with, or the welfare message with crisis support. */
export function RefusalWarning({
  warning,
  categories,
  words,
  open,
  onOpenChange,
}: {
  warning: ModerationWarning | null;
  categories: readonly ModerationCategory[];
  words: InputWords;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const crisis = warning === "crisis_resources";
  const shown = crisis
    ? [REFUSAL_WARNINGS.self_harm]
    : categories.map(category => REFUSAL_WARNINGS[category]);
  const [first] = shown;
  if (!first) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{first.title}</DialogTitle>
          {shown.map(warning => (
            <DialogDescription key={warning.small}>{warning.body(words)}</DialogDescription>
          ))}
          {crisis ? (
            <DialogDescription>
              Please contact{" "}
              <a
                href={CRISIS_RESOURCES_URL}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-2"
              >
                a crisis helpline
              </a>
              . (Don't worry — we never punish accounts for this.)
            </DialogDescription>
          ) : null}
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
